/** Import React hooks and Fuse.js for search. */
import React, {useState, useMemo} from "react";
import Fuse from "fuse.js";
import type {Variable, WaterfallItem} from "../../base/ui_general/GeneralInterfaces.tsx";
import {handleDragStartItem, handleItemDroppedBack} from "../Utils";
import type {DesignCenterTabProps} from "../Interfaces.tsx";
import {VariablesBadges} from "../../base/ui_general/GeneralFunctionComponents.tsx";
import sideStyles from './SideTabShared.module.css';


/** Props for VariablesTab component. */
interface VariableTabProps extends DesignCenterTabProps {
    variables: Array<Variable>;
    waterfall_items: Array<WaterfallItem>;
}

/** Type emitted when a variable is dropped back from FormulasTab. */
type FormulaDroppedBackDetail = { variable: string };

/** Type emitted when a variable is dropped back from TriggersTab. */
type TriggerDroppedBackDetail = { variable: string; triggerId: string };

/** Type emitted when a Waterfall item is dropped back from its section. */
type WaterfallItemDroppedBackDetail = { wfId: string; section: string; rowId: string };

/** Component for displaying draggable variables and Waterfall items. */
export function VariablesTab(props: VariableTabProps) {
    /** ---------------- Initial Waterfall items palette. ---------------- */
    const initialWaterfallItems = useMemo(
        () => props.waterfall_items.map((w) => w.name),
        [props.waterfall_items]
    );

    /** ---------------- List of all variables available in the system. ---------------- */
    const allVariables = useMemo(
        () => props.variables.map((v) => v.name),
        [props.variables]
    );

    /** ---------------- Component state. ---------------- */
    const [searchTerm, setSearchTerm] = useState("");
    const [currentWaterfallItems, setCurrentWaterfallItems] = useState(initialWaterfallItems);

    /** ---------------- Fuse.js instance for fuzzy search. ---------------- */
    const fuse = useMemo(
        () =>
            new Fuse(allVariables, {
                includeScore: true,
                threshold: 0.2,
            }),
        [allVariables]
    );

    /** ---------------- Filtered variables based on search input. ---------------- */
    const filteredVariables = useMemo(() => {
        if (!searchTerm) return allVariables;
        return fuse.search(searchTerm).map((result) => result.item);
    }, [searchTerm, fuse, allVariables]);

    const filteredVariableObjects = useMemo(
        () => props.variables.filter(v => filteredVariables.includes(v.name)),
        [filteredVariables, props.variables]
    );

    /** ---------------- Handler to allow drop. ---------------- */
    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => e.preventDefault();

    /** ---------------- Drop handler for Waterfall items returned from sections. ---------------- */
    const handleDropWaterfallItemBack = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();

        const item = e.dataTransfer.getData("item");
        const wfId = e.dataTransfer.getData("wfId");
        const section = e.dataTransfer.getData("section");
        const rowId = e.dataTransfer.getData("rowId");

        if (!item) return;

        /** Add item back to Waterfall palette if not already present. */
        if (!currentWaterfallItems.includes(item)) {
            setCurrentWaterfallItems((prev) => [...prev, item]);
            handleItemDroppedBack(item);
        }

        if (wfId && section && rowId) {
            window.dispatchEvent(
                new CustomEvent<WaterfallItemDroppedBackDetail>("waterfall-item-dropped-back", {
                    detail: {wfId, section, rowId},
                })
            );
        }
    };

    /** ---------------- Drop handler for variables returned from FormulasTab or TriggersTab. ---------------- */
    const handleVariableDropBack = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();

        const from = e.dataTransfer.getData("from"); // "FormulasTab" | "TriggersTab"
        const variable = e.dataTransfer.getData("variable");
        const fromTriggerId = e.dataTransfer.getData("fromTriggerId");

        if (!variable) return;

        /** Dispatch event for FormulasTab. */
        if (from === "FormulasTab") {
            window.dispatchEvent(
                new CustomEvent<FormulaDroppedBackDetail>("formula-variable-dropped-back", {
                    detail: {variable},
                })
            );
        }

        /** Dispatch event for TriggersTab. */
        if (from === "TriggersTab" && fromTriggerId) {
            window.dispatchEvent(
                new CustomEvent<TriggerDroppedBackDetail>("trigger-variable-dropped-back", {
                    detail: {variable, triggerId: fromTriggerId},
                })
            );
        }
    };

    const handleVariableBadgeDragStart = (variable: Variable, e: React.DragEvent) => {
        e.dataTransfer.setData("variable", variable.name);
        e.dataTransfer.setData("from", "VariablesTab");
    };

    /** ---------------- Render component. ---------------- */
    return (
        <div className={sideStyles.sideTabContainer}>
            <div className={sideStyles.sideHeaderSticky}>Variables & Items</div>
            <div className={`${sideStyles.sideContentArea} ${sideStyles.sideGrid}`}>
                {/* Waterfall Items (30%) */}
                <div
                    onDrop={handleDropWaterfallItemBack}
                    onDragOver={handleDragOver}
                    className={`${sideStyles.sideCard} ${sideStyles.sideCardBodyPadding} ${sideStyles.sideCardColumn}`}
                >
                    <h5 className={sideStyles.sideSectionTitle}>Waterfall Items</h5>
                    <div className={sideStyles.sideScrollY}>
                        {currentWaterfallItems.map((item) => (
                            <div
                                key={item}
                                className={sideStyles.sideDraggableItem}
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData("item", item);
                                    e.dataTransfer.setData("from", "VariablesTab");
                                    handleDragStartItem(e, item);
                                }}
                            >
                                {item}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Variables (70%) */}
                <div
                    onDrop={handleVariableDropBack}
                    onDragOver={handleDragOver}
                    className={`${sideStyles.sideCard} ${sideStyles.sideCardBodyPadding} ${sideStyles.sideCardColumn}`}
                >
                    <h5 className={sideStyles.sideSectionTitle}>Variables</h5>
                    <input
                        type="text"
                        placeholder="Search variables..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={sideStyles.sideSearchInput}
                    />
                    {/* Scroll only for the variable list */}
                    <div className={sideStyles.sideScrollY}>
                        <VariablesBadges
                            variables={filteredVariableObjects}
                            draggable
                            onDragStart={handleVariableBadgeDragStart}
                            lightWeight={true}
                        />
                        {filteredVariableObjects.length === 0 && (
                            <div className={sideStyles.sideEmptyState}>No results found</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
